import { fetchFormsForAutocomplete } from "@rpforms/shared";
import {
    createOfferFromForms,
    dueNotesForPayload,
    IFormPayload,
    parseSchemaFields,
} from "@rpforms/shared/build/utilities";
import { user } from "@rpforms/shared/build/actions/actions_helper";
import { fetchFormEntryPdfUrl, openSignedUrl } from "@rpforms/shared/build/actions/signedDownloads";
import axios from "axios";
import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { API_URL, COMPANY_ID } from "../config";
import { RootState } from "../reducers";

export type GenerateTarget = "offer" | "invoice";

const LOAD_CONCURRENCY = 5;

interface ICachedEntry {
    formEntry?: any;
    formPayload?: IFormPayload;
}

interface IGenerateOptions {
    cache?: Map<number, ICachedEntry>;
    forms?: any[];
    fallbackCustomerId?: any;
    fallbackEntityId?: any;
    onEntryLoaded?: (entryId: number, formEntry: any, formPayload: IFormPayload) => void;
}

interface IEntryNotes {
    entryId: number;
    entryName: string;
    formName: string;
    notes: string[];
}

const buildFormPayload = (entryId: number, formEntry: any): IFormPayload => {
    if (!formEntry) {
        throw new Error(`Form entry ${entryId} not found in database`);
    }

    // Get schema from form_revision.form_data (contains field definitions)
    let schemaFields = parseSchemaFields(formEntry?.form_revision?.form_data);
    if (schemaFields.length === 0) {
        schemaFields = parseSchemaFields(formEntry?.latest_revision?.form_data);
    }
    if (schemaFields.length === 0) {
        throw new Error(`Failed to parse schema for entry ${entryId}`);
    }

    // Get entry values from form_data (contains user input values)
    let entryValues: any = formEntry?.form_data || {};
    if (typeof entryValues === "string") {
        try {
            entryValues = JSON.parse(entryValues);
        } catch (e) {
            entryValues = {};
        }
    }

    // Apply entry values to schema fields recursively
    const applyEntryValues = (fields = []) => {
        return fields.map((field) => {
            const nextField = { ...field };
            if (
                entryValues &&
                nextField?.hashCode &&
                Object.prototype.hasOwnProperty.call(entryValues, nextField.hashCode)
            ) {
                nextField.value = entryValues[nextField.hashCode];
            }
            if (
                nextField?.type === "InventoryItemSelectField" ||
                nextField?.type === "LeistungsV"
            ) {
                const notesKey = `${nextField.hashCode}__notes`;
                if (entryValues && entryValues[notesKey]) {
                    nextField.notes = entryValues[notesKey];
                }
            }
            // Handle InventoryItemSelectTableRow inside TableField
            if (nextField?.type === "TableField" && nextField.subfields) {
                nextField.subfields = nextField.subfields.map((subfield) => {
                    const nextSubfield = { ...subfield };
                    if (nextSubfield?.type === "InventoryItemSelectTableRow") {
                        const notesKey = `${nextSubfield.hashCode}__notes`;
                        if (entryValues && entryValues[notesKey]) {
                            nextSubfield.notes = entryValues[notesKey];
                        }
                    }
                    return nextSubfield;
                });
            }
            if (nextField.subfields && nextField.subfields.length > 0) {
                nextField.subfields = applyEntryValues(nextField.subfields);
            }
            return nextField;
        });
    };

    return {
        fields: applyEntryValues(schemaFields),
        form: {
            name: formEntry.form?.name || formEntry.form?.label || "",
            endpoint: "",
        },
        entryName: formEntry?.name || "",
    };
};

const extractInventoryNotes = (fields = [], notes: string[] = []) => {
    fields.forEach((field) => {
        if (field?.type === "InventoryItemSelectField" || field?.type === "LeistungsV") {
            if (typeof field.notes === "string" && field.notes.trim().length > 0) {
                notes.push(field.notes.trim());
            }
        }
        if (field?.type === "TableField" && field.value) {
            try {
                const tableData =
                    typeof field.value === "string" ? JSON.parse(field.value) : field.value;
                if (Array.isArray(tableData)) {
                    tableData.forEach((row, rowIndex) => {
                        if (!Array.isArray(row)) return;
                        row.forEach((cell) => {
                            if (typeof cell?.notes === "string" && cell.notes.trim().length > 0) {
                                notes.push(`Zeile ${rowIndex + 1}: ${cell.notes.trim()}`);
                            }
                        });
                    });
                }
            } catch (e) {
                // Failed to parse table data
            }
        }
        if (field?.type !== "TableField" && Array.isArray(field?.subfields)) {
            extractInventoryNotes(field.subfields, notes);
        }
    });
    return notes;
};

const refreshInventoryPrices = async (offerData: any) => {
    const positions = offerData?.invoicePositions || [];
    const inventoryItemIds = Array.from(
        new Set(
            positions
                .map((position) => position.inventory_item_id)
                .map((id) => {
                    if (typeof id === "string") {
                        const parsed = parseInt(id, 10);
                        return Number.isFinite(parsed) ? parsed : null;
                    }
                    if (typeof id === "number" && Number.isFinite(id)) {
                        return id;
                    }
                    return null;
                })
                .filter((id): id is number => id !== null)
        )
    );

    const inventoryItemClientUuids = Array.from(
        new Set<string>(
            positions
                .filter((position) => !position.inventory_item_id)
                .map((position) => position.inventory_item_client_uuid)
                .filter((uuid): uuid is string => typeof uuid === "string" && !!uuid)
        )
    );

    if (inventoryItemIds.length === 0 && inventoryItemClientUuids.length === 0) {
        return offerData;
    }

    try {
        const fetchInventoryItems = async (query: string) => {
            const response = await axios.get(
                `${API_URL}/companies/${COMPANY_ID}/inventory_items.json?${query}&page_size=99999`,
                {
                    headers: { authorization: user().token },
                }
            );
            return (response?.data?.inventory_items || []) as Array<any>;
        };
        const [itemsById, itemsByClientUuid] = await Promise.all([
            inventoryItemIds.length > 0
                ? fetchInventoryItems(
                      inventoryItemIds
                          .map((id) => `ids[]=${encodeURIComponent(String(id))}`)
                          .join("&")
                  )
                : Promise.resolve([]),
            inventoryItemClientUuids.length > 0
                ? fetchInventoryItems(
                      inventoryItemClientUuids
                          .map((uuid) => `client_uuids[]=${encodeURIComponent(uuid)}`)
                          .join("&")
                  )
                : Promise.resolve([]),
        ]);
        const inventoryItemMap = new Map(itemsById.map((item) => [item.id, item]));
        const clientUuidMap = new Map(itemsByClientUuid.map((item) => [item.client_uuid, item]));

        return {
            ...offerData,
            invoicePositions: positions.map((position) => {
                const inventoryId = position.inventory_item_id;
                const inventoryItem = (inventoryId
                    ? inventoryItemMap.get(
                          typeof inventoryId === "string" ? parseInt(inventoryId, 10) : inventoryId
                      )
                    : clientUuidMap.get(position.inventory_item_client_uuid)) as any;

                if (!inventoryItem) {
                    return inventoryId ? { ...position, inventory_item_missing: true } : position;
                }

                const hasPrice = typeof position.price === "number" && position.price > 0;
                const salesPrice =
                    typeof inventoryItem.sales_price === "number" ? inventoryItem.sales_price : 0;

                return {
                    ...position,
                    inventory_item_id: inventoryItem.id,
                    inventory_item_missing: false,
                    price: !hasPrice && salesPrice > 0 ? salesPrice : position.price,
                };
            }),
        };
    } catch (e) {
        console.error("Failed to fetch inventory item updates", e);
        return offerData;
    }
};

export const useGenerateOffer = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const entitiesDB = useSelector<RootState, any>((rootState) => rootState.entities);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notesByEntry, setNotesByEntry] = useState<IEntryNotes[]>([]);
    const [pendingQuery, setPendingQuery] = useState<string | null>(null);

    const loadForms = (forms?: any[]) =>
        forms && forms.length > 0
            ? Promise.resolve(forms)
            : new Promise<any[]>((resolve, reject) => {
                  (dispatch(fetchFormsForAutocomplete((data) => resolve(data))) as any).catch(
                      reject
                  );
              });

    const loadEntry = async (entryId: number) => {
        let formEntry;
        try {
            const response = await axios.get(`${API_URL}/form_entries/${entryId}.json`, {
                headers: { authorization: user().token },
            });
            formEntry = response.data;
        } catch (e) {
            throw new Error(`Form entry ${entryId} not found`);
        }
        return { formEntry, formPayload: buildFormPayload(entryId, formEntry) };
    };

    const generate = async (
        target: GenerateTarget,
        entryIds: number[],
        options: IGenerateOptions = {}
    ) => {
        const cache = options.cache || new Map<number, ICachedEntry>();

        // Clear any existing generated offer data to prevent spillover from previous sessions
        sessionStorage.removeItem("generated_offer_data");
        sessionStorage.removeItem("generated_offer_sources");

        setIsGenerating(true);
        setError(null);

        try {
            console.log(
                "[Generate Offer] Starting offer generation for",
                entryIds.length,
                "entries"
            );

            const formPayloads: IFormPayload[] = [];
            const entries = new Map<number, any>();

            const loaded = new Map<number, IFormPayload>();
            const missing = entryIds.filter((entryId) => {
                const cached = cache.get(entryId);
                if (cached?.formEntry) {
                    entries.set(entryId, cached.formEntry);
                }
                if (cached?.formPayload?.fields) {
                    loaded.set(entryId, {
                        ...cached.formPayload,
                        entryName: cached.formPayload.entryName || cached.formEntry?.name || "",
                    });
                    return false;
                }
                return true;
            });

            for (let i = 0; i < missing.length; i += LOAD_CONCURRENCY) {
                const batch = missing.slice(i, i + LOAD_CONCURRENCY);
                const results = await Promise.all(batch.map((entryId) => loadEntry(entryId)));
                results.forEach(({ formEntry, formPayload }, index) => {
                    const entryId = batch[index];
                    entries.set(entryId, formEntry);
                    loaded.set(entryId, formPayload);
                    if (options.onEntryLoaded) {
                        options.onEntryLoaded(entryId, formEntry, formPayload);
                    }
                });
            }
            entryIds.forEach((entryId) => formPayloads.push(loaded.get(entryId)));

            if (formPayloads.length === 0) {
                throw new Error("No form data available to generate offer");
            }

            const invalidPayloads = formPayloads.filter(
                (payload) => !payload || !Array.isArray(payload.fields)
            );
            if (invalidPayloads.length > 0) {
                throw new Error(
                    `${invalidPayloads.length} form payload(s) are missing required data`
                );
            }

            let result;
            try {
                result = createOfferFromForms(formPayloads, {
                    defaultUnit: "repair",
                    defaultPrice: 0,
                    includeHintsInDescription: true,
                });
            } catch (createError) {
                console.error("[Generate Offer] createOfferFromForms failed:", createError);
                throw new Error(
                    `Failed to create offer: ${createError.message || "Unknown error"}`
                );
            }

            const entriesWithInventoryNotes: IEntryNotes[] = [];
            entryIds.forEach((entryId, index) => {
                const payload = formPayloads[index];
                if (!payload) return;

                const notes = [
                    ...extractInventoryNotes(payload.fields || []),
                    ...dueNotesForPayload(payload),
                ];
                if (notes.length > 0) {
                    entriesWithInventoryNotes.push({
                        entryId,
                        entryName: entries.get(entryId)?.name || `Eintrag ${entryId}`,
                        formName: payload.form?.name || "Unbekanntes Formular",
                        notes,
                    });
                }
            });

            const offerData = await refreshInventoryPrices(result.offerData);

            sessionStorage.setItem("generated_offer_data", JSON.stringify(offerData));
            sessionStorage.setItem("generated_offer_sources", JSON.stringify(entryIds));

            const selectedEntries = entryIds.map((id) => entries.get(id)).filter(Boolean);
            const customerIds = Array.from(
                new Set(selectedEntries.map((entry) => entry.customer_id).filter((id) => !!id))
            );
            const entityIds = Array.from(
                new Set(selectedEntries.map((entry) => entry.entity_id).filter((id) => !!id))
            );

            let customer_id = customerIds.length === 1 ? customerIds[0] : null;
            const entity_id =
                (entityIds.length === 1 ? entityIds[0] : null) || options.fallbackEntityId || null;

            if (!customer_id && entity_id && Array.isArray(entitiesDB?.autocomplete)) {
                const entityMatch = entitiesDB.autocomplete.find(
                    (entity) => entity.id === entity_id
                );
                if (entityMatch?.customer_id) {
                    customer_id = entityMatch.customer_id;
                }
            }
            if (!customer_id) {
                customer_id = options.fallbackCustomerId || null;
            }

            // The prefill machinery is form-agnostic: buildOfferFormData injects the extracted
            // LV positions into whichever form is loaded — only the target form_id differs
            // between Angebot (000_Angebot) and Rechnung (000_Rechnung).
            const targetFormName = target === "invoice" ? "000_Rechnung" : "000_Angebot";
            const forms = await loadForms(options.forms);
            const targetForm = forms.find(
                (f) => f.name === targetFormName || f.label === targetFormName
            );

            let query = `prefill=generated_offer`;
            if (targetForm) query += `&form_id=${encodeURIComponent(targetForm.id)}`;
            if (customer_id) query += `&customer_id=${encodeURIComponent(customer_id)}`;
            if (entity_id) query += `&entity_id=${encodeURIComponent(entity_id)}`;

            if (entriesWithInventoryNotes.length > 0) {
                setNotesByEntry(entriesWithInventoryNotes);
                setPendingQuery(query);
                return;
            }
            history.push(`/entries/editor?${query}`);
        } catch (err) {
            console.error("[Generate Document Error]", err);
            let errorMessage = `Fehler beim Generieren ${
                target === "invoice" ? "der Rechnung" : "des Angebots"
            }`;

            if (err instanceof Error) {
                errorMessage += `: ${err.message}`;
            } else if (typeof err === "string") {
                errorMessage += `: ${err}`;
            }

            if (err?.message?.includes("parse") || err?.message?.includes("JSON")) {
                errorMessage =
                    "Fehler beim Verarbeiten der Formulardaten. Möglicherweise sind die Daten beschädigt.";
            } else if (err?.message?.includes("not found")) {
                errorMessage =
                    "Mindestens eines der ausgewählten Formulare konnte nicht gefunden werden.";
            } else if (err?.message?.includes("schema")) {
                errorMessage =
                    "Fehler beim Lesen des Formularschemas. Bitte versuchen Sie es erneut.";
            }

            setError(errorMessage);
        } finally {
            setIsGenerating(false);
        }
    };

    const closeNotes = () => {
        setNotesByEntry([]);
        setPendingQuery(null);
    };

    const notesModal = (
        <Modal show={notesByEntry.length > 0} onHide={closeNotes} centered>
            <Modal.Header closeButton>
                <Modal.Title>Hinweis</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    Die Protokolle enthalten Hinweise zum Leistungsverzeichnis oder fällige Geräte,
                    deren Austausch ergänzt wurde. Bitte vor dem Angebot prüfen.
                </p>
                {notesByEntry.map((entry) => (
                    <div
                        key={`${entry.entryName}-${entry.formName}`}
                        style={{ marginBottom: "12px" }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: "12px",
                            }}
                        >
                            <strong style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                                {entry.entryName} ({entry.formName})
                            </strong>
                            <Button
                                onClick={() => openSignedUrl(fetchFormEntryPdfUrl(entry.entryId))}
                                size="sm"
                                variant="outline-primary"
                                style={{ flexShrink: 0, whiteSpace: "nowrap" }}
                            >
                                PDF in neuem Tab
                            </Button>
                        </div>
                        <ul style={{ marginTop: "6px" }}>
                            {entry.notes.map((note, index) => (
                                <li key={`${entry.entryName}-${index}`}>{note}</li>
                            ))}
                        </ul>
                    </div>
                ))}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="primary"
                    onClick={() => {
                        const query = pendingQuery;
                        closeNotes();
                        if (query) {
                            history.push(`/entries/editor?${query}`);
                        }
                    }}
                >
                    Weiter
                </Button>
            </Modal.Footer>
        </Modal>
    );

    return { generate, isGenerating, error, setError, notesModal };
};
